import { zodResolver } from "@hookform/resolvers/zod";
import axios from "axios";
import _ from "lodash";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import getLogsFromAlcoholData from "src/lib/getLogsFromAlcoholData";
import {
  AlcoholData,
  deleteAlcoholDataFromDay,
  syncProperties,
} from "src/lib/slices/alcoholSlice";
import { createSyncData } from "src/lib/sync";
import { z } from "zod";
import SessionLogCounter from "./SessionLogCounter";
import { Button } from "./ui/button";
import { AlcDrawer, DrawerDescription, DrawerTitle } from "./ui/drawer";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "./ui/form";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";

const FormSchema = z.object({
  name: z.string().min(1).max(50),
  public: z.boolean(),
  dates: z.array(z.string()).min(1, {
    message: "Musíš vybrat alespoň jeden den pro uložení.",
  }),
});

const SaveSessionDrawer = ({
  children,
  mutate,
  defaultDates,
}: {
  children: React.ReactNode;
  mutate?: () => void;
  defaultDates?: string[];
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const dispatch = useDispatch();
  const alcoholState: AlcoholData = useSelector((state: any) => state.alcohol);
  const user = useSelector((state: any) => state.user);

  const [saving, setSaving] = useState(false);

  const logs = getLogsFromAlcoholData(alcoholState);

  const dates: string[] = useMemo(() => {
    const dates = new Set<string>();
    logs.forEach((log) => {
      dates.add(new Date(log.data.date).toLocaleDateString("cs"));
    });
    return [...dates];
  }, [logs]);

  const form = useForm<z.infer<typeof FormSchema>>({
    mode: "onBlur",
    resolver: zodResolver(FormSchema),
    defaultValues: {
      name: "",
      public: false,
      dates: defaultDates ? defaultDates : dates,
    },
  });

  const selectedDates = form.watch("dates");

  const alcoholStateToSave: AlcoholData = useMemo(() => {
    const alcoholStateToSave = _.cloneDeep(alcoholState);
    alcoholStateToSave.DrunkDrinks = alcoholState.DrunkDrinks.filter((drink) =>
      selectedDates.includes(new Date(drink.date).toLocaleDateString("cs"))
    );
    alcoholStateToSave.route = alcoholState.route.filter((route) =>
      selectedDates.includes(new Date(route.timestamp).toLocaleDateString("cs"))
    );
    alcoholStateToSave.events = alcoholState.events.filter((event) =>
      selectedDates.includes(new Date(event.date).toLocaleDateString("cs"))
    );
    alcoholStateToSave.photos = alcoholState.photos.filter((photo) =>
      selectedDates.includes(new Date(photo.date).toLocaleDateString("cs"))
    );
    return alcoholStateToSave;
  }, [alcoholState, selectedDates]);

  async function onSubmit(data: z.infer<typeof FormSchema>) {
    setSaving(true);
    data = _.omitBy(data, (v) => v === null || v === "");

    const response = await axios
      .post(
        `${process.env.REACT_APP_API_URL}/sessions/save`,
        {
          name: data.name,
          public: data.public,
          alcoholData: createSyncData(alcoholStateToSave, syncProperties),
        },
        { headers: { Authorization: user.token } }
      )
      .catch(() => {
        toast.error(
          "Nastala chyba. Seanci se nepodařilo uložit. Zkontroluj připojení k internetu."
        );
      });
    if (response) {
      setIsOpen(false);
      data.dates.forEach((date) => {
        dispatch(deleteAlcoholDataFromDay({ date }));
      });
      toast(
        "Seance byla úspěšně uložena. Svoje seance si můžeš prohlídnout na stránce seancí. Doufejme, že se dáš do další seance již brzy.",
        { duration: 10000 }
      );
      mutate?.();
    }
    setSaving(false);
  }

  const photos = logs
    .map((log) => log.data.photoData)
    .filter((photo) => !!photo);

  const notUploadedPhotos = photos.filter((photo) => photo.startsWith("data:"));

  return (
    <AlcDrawer
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      trigger={children}
      asChild
      header={
        <>
          <DrawerTitle className="text-center">Uložení seance</DrawerTitle>
          <DrawerDescription asChild>
            <div className="space-y-2">
              <SessionLogCounter
                alcoholData={alcoholStateToSave}
                className="flex items-center flex-col"
              />
              {notUploadedPhotos.length > 0 && (
                <div style={{ color: "var(--error)", textAlign: "center" }}>
                  Některé obrázky ještě nebyly nahrány na cloud. Zkus to prosím
                  později.
                </div>
              )}
            </div>
          </DrawerDescription>
        </>
      }
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Název</FormLabel>
                <Input
                  type="text"
                  autoComplete="name"
                  id="name"
                  maxLength={50}
                  {...field}
                />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="public"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
                <div className="space-y-0.5">
                  <FormLabel>Veřejné</FormLabel>
                  <FormDescription>
                    Seance bude vidět na tvém profilu.
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="dates"
            render={({ field }) => (
              <div>
                <div className="flex gap-2 flex-col rounded-lg border p-3 shadow-sm">
                  {dates.map((date) => (
                    <div
                      className="flex flex-row items-center justify-between"
                      key={date}
                    >
                      <FormLabel>{date}</FormLabel>
                      <Switch
                        checked={field.value.includes(date)}
                        onCheckedChange={(checked) => {
                          const dates = field.value;
                          if (checked) {
                            field.onChange([...dates, date]);
                          } else {
                            field.onChange(dates.filter((d) => d !== date));
                          }
                        }}
                      />
                    </div>
                  ))}
                </div>
                <FormMessage />
              </div>
            )}
          />

          <div>
            <Button
              type="submit"
              className="w-full"
              disabled={notUploadedPhotos.length > 0 || saving}
            >
              {saving ? "Ukládání" : "Uložit seanci"}
            </Button>
          </div>
        </form>
      </Form>
    </AlcDrawer>
  );
};

export default SaveSessionDrawer;
